'use client';

import React from 'react';
import { styled } from '@linaria/react';
import { useSpring, animated } from 'react-spring';

import { BREAKPOINTS } from '@/constants';

/**
 * Steps:
 * 0: Only Second
 * 1: Only First
 * 2: Both side-by-side (desktop only)
 */
type Step = 0 | 1 | 2;
interface Props extends React.HTMLAttributes<HTMLDivElement> {
  maxImageWidth?: string;
  // When in step 0 or 1, the two images are stacked. By default, their headings are 100% wide, but we can tailor this to match the length of the longer heading. This produces a slightly nicer UX when toggling "side-by-side" (step 3), since there isn't a mysterious full-width rectangle the same color as the background obstructing the animation.
  longerHeadingLength?: string;
  firstTitle: string;
  firstChild: React.ReactNode;
  secondTitle: string;
  secondChild: React.ReactNode;
  includeExpandBtn?: boolean;
  initialStep?: Step;
}

function ImageCompare({
  maxImageWidth,
  longerHeadingLength,
  firstTitle,
  firstChild,
  secondTitle,
  secondChild,
  includeExpandBtn = true,
  initialStep = 0,
  ...delegated
}: Props) {
  const [step, setStep] = React.useState<Step>(initialStep);

  const firstStyles = useSpring({
    position: step === 0 ? 'absolute' : 'relative',
    top: 0,
    left: 0,
    transform:
      step === 2
        ? `scale(0.5) translate(-51.25%, 0)`
        : `scale(1) translate(0%, 0%)`,

    config:
      step === 2
        ? {
            tension: 210,
            friction: 30,
          }
        : {
            tension: 210,
            friction: 28,
          },
  });

  const secondStyles = useSpring({
    position: step === 0 ? 'relative' : 'absolute',
    top: 0,
    left: 0,
    transform:
      step === 2
        ? `scale(0.5) translate(51.25%, 0)`
        : `scale(1) translate(0%, 0%)`,

    config:
      step === 2
        ? {
            tension: 180,
            friction: 32,
          }
        : {
            tension: 240,
            friction: 30,
          },
  });

  return (
    <Wrapper {...delegated}>
      <InnerWrapper
        style={{
          maxWidth: maxImageWidth,
          '--longer-heading-length': longerHeadingLength,
        }}
      >
        <Column
          // TS is unhappy because `position` is set to "SpringValue<string>" instead of `relative | absolute | ...`. I don't think this actually matters.
          // @ts-ignore
          style={{
            ...firstStyles,
            zIndex: step === 0 ? 0 : 1,
          }}
        >
          <Heading>{firstTitle}</Heading>
          <ImageWrapper>{firstChild}</ImageWrapper>
        </Column>
        <Column
          // TS is unhappy because `position` is set to "SpringValue<string>" instead of `relative | absolute | ...`. I don't think this actually matters.
          // @ts-ignore
          style={{
            ...secondStyles,
            zIndex: step === 0 ? 1 : 0,
          }}
        >
          <Heading>{secondTitle}</Heading>
          <ImageWrapper>{secondChild}</ImageWrapper>
        </Column>
      </InnerWrapper>
      <ButtonWrapper>
        <PrimaryButton
          disabled={step === 2}
          onClick={() => {
            const nextStep = step === 0 ? 1 : 0;
            setStep(nextStep);
          }}
        >
          Toggle
        </PrimaryButton>
        {includeExpandBtn && (
          <SecondaryButton
            onClick={() => {
              setStep(step === 2 ? 1 : 2);
            }}
          >
            {step === 2 ? 'Collapse' : 'Expand'}
          </SecondaryButton>
        )}
      </ButtonWrapper>
    </Wrapper>
  );
}

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  border: 1px solid var(--color-gray-300);
  padding: 32px;
  padding-top: 24px;
  margin-top: 24px;
  margin-bottom: 48px;

  @media ${BREAKPOINTS.mdAndLarger} {
    border-radius: 8px;
    margin-left: -32px;
    margin-right: -32px;
  }
`;

const InnerWrapper = styled.div`
  width: 100%;
  position: relative;
  display: flex;
`;

const ButtonWrapper = styled.div`
  display: flex;
  gap: 16px;
`;

const Button = styled.button`
  font-size: 1rem;
  padding: 0.75rem 0;
  width: 8rem;
  border-radius: 4px;
  font-weight: var(--font-weight-medium);
  text-align: center;
`;

const PrimaryButton = styled(Button)`
  background-color: var(--color-gray-800);
  color: var(--color-background);
  transition: opacity 200ms;

  &:not(:disabled):hover {
    background-color: var(--color-gray-900);
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const SecondaryButton = styled(Button)`
  background: transparent;
  color: var(--color-text);
  border: 1px solid var(--color-gray-300);

  &:not(:disabled):hover {
    background-color: var(--color-gray-100);
  }

  @media ${BREAKPOINTS.mdAndSmaller} {
    display: none;
  }
`;

const Column = styled(animated.div)`
  flex: 1;
  will-change: transform;
  transform-origin: center center;
`;

const Heading = styled.h3`
  font-size: 1.5rem;
  font-weight: bold;
  margin-bottom: 8px;
  background: var(--color-background);
  width: var(--longer-heading-length, 100%);
  max-width: 100%;
`;

const ImageWrapper = styled.div`
  border-radius: 4px;
  overflow: clip;
  box-shadow:
    0px 1px 2px hsl(0deg 0% 0% / 0.06),
    0px 4px 8px hsl(0deg 0% 0% / 0.06),
    0px 16px 32px hsl(0deg 0% 0% / 0.07);
  margin-bottom: 32px;
`;

export default ImageCompare;
